import './Stepper.css';

interface StepperProps {
  id: string;
  value: number | '';
  min?: number;
  max?: number;
  onChange: (value: number | '') => void;
  decrementLabel: string;
  incrementLabel: string;
  /** Optional — freezes both buttons and the input (e.g. viewing an Archived payment plan, T60).
   * Defaults to `false`; existing call sites are unaffected. */
  disabled?: boolean;
}

/** 10-design-system.md §4.12 — `[ − ] N [ + ]`, buttons ≥40×40px, current value in a live region
 * alongside a keyboard-editable numeric input (never stepper-button-only). */
export function Stepper({
  id,
  value,
  min = 1,
  max,
  onChange,
  decrementLabel,
  incrementLabel,
  disabled = false,
}: StepperProps) {
  function clamp(n: number): number {
    let result = n;
    if (min !== undefined) result = Math.max(min, result);
    if (max !== undefined) result = Math.min(max, result);
    return result;
  }

  function step(delta: number) {
    const base = value === '' ? min - delta : value;
    onChange(clamp(base + delta));
  }

  return (
    <div className="fa-stepper">
      <button
        type="button"
        className="fa-stepper__btn"
        aria-label={decrementLabel}
        onClick={() => step(-1)}
        disabled={disabled || (value !== '' && value <= min)}
      >
        −
      </button>
      <input
        id={id}
        type="number"
        className="fa-stepper__input"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        aria-live="polite"
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === '') {
            onChange('');
            return;
          }
          const n = Number(e.target.value);
          if (!Number.isNaN(n)) onChange(clamp(n));
        }}
      />
      <button
        type="button"
        className="fa-stepper__btn"
        aria-label={incrementLabel}
        onClick={() => step(1)}
        disabled={disabled}
      >
        +
      </button>
    </div>
  );
}
